export const DEFAULT_LOCALE = 'en-AU' as const
export const US_LOCALE = 'en-US' as const
export const US_REGION_CODE = 'US' as const
export const GERMAN_LOCALE = 'de-DE' as const
export const RTL_LOCALES = ['ar', 'he', 'fa', 'ur'] as const

export type LocaleDirection = 'ltr' | 'rtl'

export interface VisibleLanguageOption {
  locale: string
  nativeName: string
  slug?: string
  canonical?: boolean
}

interface LanguageRemoteConfigValue {
  visibleLanguages?: Array<VisibleLanguageOption>
  preReleaseLanguages?: Array<VisibleLanguageOption>
}
interface LanguageAlternateLink {
  hrefLang: string
  href: string
}

function getMatchingLanguageOption(
  locale: string,
  languageOptions: Array<VisibleLanguageOption>,
): VisibleLanguageOption | undefined {
  const normalisedLocale = locale.replace(/_/g, '-')
  const exactMatch = languageOptions.find((option) => {
    return normalisedLocale === option.slug || normalisedLocale === option.locale
  })
  if (exactMatch) return exactMatch

  // Where locale is unsupported outside the main version
  // it should redirect to base locale prefix like fr-CA to fr
  const language = normalisedLocale.split('-')[0]
  const sameLanguageOptions = languageOptions.filter((option) => {
    return option.slug === language || option.locale.split('-')[0] === language
  })
  if (sameLanguageOptions.length <= 1) return sameLanguageOptions[0]

  // A language can be written in more than one script, e.g. zh-HK and zh-Hant are
  // Traditional Chinese while zh-CN is Simplified, so prefer an option in the same script
  const script = getLikelyScript(normalisedLocale)
  const sameScriptOption = script
    ? sameLanguageOptions.find(option => getLikelyScript(option.locale) === script)
    : undefined
  return sameScriptOption ?? sameLanguageOptions[0]
}

/**
 * The script a locale is most likely written in, e.g. Hant for zh-HK and Hans for zh-CN.
 * Undefined for a malformed tag, since URL segments and headers aren't always valid locales.
 */
function getLikelyScript(locale: string): string | undefined {
  try {
    return new Intl.Locale(locale).maximize().script
  } catch {
    return undefined
  }
}

export function getCountryCodeFromLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const match = getMatchingLanguageOption(locale, languageOptions)
  if (match) return new Intl.Locale(match.locale).region
}

export function getNativeNameForLocale(locale: string, languageOptions: Array<VisibleLanguageOption>): string | undefined {
  const match = getMatchingLanguageOption(locale, languageOptions)
  if (match) return match.nativeName
}

export function getNormalisedLocale(
  unnormalisedLocale: string,
  languageOptions?: Array<VisibleLanguageOption>,
): string | null {
  if (!languageOptions?.length) return null
  return resolveLocale(unnormalisedLocale, languageOptions).normalised
}

export function resolveLocale(
  locale: string | null | undefined,
  languageOptions: Array<VisibleLanguageOption>,
): {
  full: string | null
  normalised: string | null
} {
  if (!locale) return { full: null, normalised: null }

  const match = getMatchingLanguageOption(locale, languageOptions)
  if (!match) return { full: null, normalised: null }

  return {
    full: match.locale,
    normalised: match.slug ?? match.locale,
  }
}

/**
 * The locale to use when a request has no explicit locale preference (no URL locale,
 * `lang` cookie, account language or negotiated browser locale).
 *
 * The US region defaults to US English when it is an available language option,
 * every other region (and the US when `en-US` is unavailable) defaults to `en-AU`.
 */
export function getDefaultLocaleForRegion(
  regionCode: string | undefined,
  languageOptions: Array<VisibleLanguageOption>,
): string {
  if (regionCode !== US_REGION_CODE) return DEFAULT_LOCALE

  const hasUsLocale = languageOptions.some(option => option.locale === US_LOCALE)
  return hasUsLocale ? US_LOCALE : DEFAULT_LOCALE
}

export function getLanguageOptions(
  remoteConfig: App.LabRemoteConfigResult | undefined,
  isStaff = false,
): {
  languageOptions: Array<VisibleLanguageOption>
  preReleaseLanguageOptions: Array<VisibleLanguageOption>
} {
  const value = getLanguageRemoteConfigValue(remoteConfig)
  const releasedLanguageOptions = value?.visibleLanguages ?? []
  const preReleaseLanguageOptions = isStaff && Array.isArray(value?.preReleaseLanguages)
    ? value.preReleaseLanguages
    : []

  return {
    languageOptions: [...releasedLanguageOptions, ...preReleaseLanguageOptions],
    preReleaseLanguageOptions,
  }
}

export function getLocaleDirection(
  locale: string | null | undefined,
): LocaleDirection {
  if (!locale) return 'ltr'
  const language = locale.replace(/_/g, '-').split('-')[0].toLowerCase()
  return RTL_LOCALES.some((rtlLocale) => rtlLocale === language) ? 'rtl' : 'ltr'
}

export function getLocaleLanguageCode(
  locale: string | null | undefined,
): string {
  return (locale ?? DEFAULT_LOCALE).replace(/_/g, '-').split('-')[0].toLowerCase()
}

/**
 * The full language tag for an `<html lang>` attribute, e.g. zh-TW rather than zh.
 * Browsers use the region and script to choose glyphs and fallback fonts (Simplified
 * and Traditional Chinese draw some characters differently).
 */
export function getHtmlLang(
  locale: string | null | undefined,
): string {
  const languageTag = (locale ?? DEFAULT_LOCALE).replace(/_/g, '-')
  try {
    return Intl.getCanonicalLocales(languageTag)[0]
  } catch {
    return getLocaleLanguageCode(locale)
  }
}

/**
 * Chinese locales share a language subtag but differ in script, so they still
 * need an explicit choice. Every other shared family, such as en-AU and en-US,
 * does not.
 */
const LOCALE_FAMILIES_REQUIRING_CONFLICT_CONFIRMATION = new Set(['zh'])

export function requiresLocaleConflictConfirmation(
  leftLocale: string,
  rightLocale: string,
): boolean {
  const leftLanguage = getLocaleLanguageCode(leftLocale)
  const rightLanguage = getLocaleLanguageCode(rightLocale)
  if (leftLanguage !== rightLanguage) return true

  return LOCALE_FAMILIES_REQUIRING_CONFLICT_CONFIRMATION.has(leftLanguage)
}

/**
 * Arabic content in the customer portal uses Western digits for numeric values.
 * Preserve every other locale preference while applying that number-system policy.
 */
export function getLocaleWithWesternDigits(locale: string): string {
  if (getLocaleLanguageCode(locale) !== 'ar') return locale

  return new Intl.Locale(locale, { numberingSystem: 'latn' }).toString()
}

export function isEnglishLocale(locale: string | null | undefined): boolean {
  return getLocaleLanguageCode(locale) === 'en'
}

/**
 * Prefer an explicit non-default request locale; otherwise use the locale baked into
 * locale-specific browser bundles (`BUILD_LOCALE`) so formatting matches inlined translations.
 */
export function resolveActiveAppLocale(
  geoLocale: string | null | undefined,
): string | undefined {
  if (geoLocale && geoLocale !== DEFAULT_LOCALE) {
    return geoLocale
  }

  const buildLocale = process.env.BUILD_LOCALE
  if (buildLocale) {
    return buildLocale
  }

  return geoLocale ?? undefined
}

function getLanguageRemoteConfigValue(
  remoteConfig: App.LabRemoteConfigResult | undefined,
): LanguageRemoteConfigValue | undefined {
  if (remoteConfig?.error) return
  const value = remoteConfig?.value
  if (!value || typeof value !== 'object' || Array.isArray(value)) return

  return value as LanguageRemoteConfigValue
}

export function getLanguageAlternateLinks({
  customerPortalURL,
  path,
  languageOptions,
}: {
  customerPortalURL: string
  path: string
  languageOptions: Array<VisibleLanguageOption>
}): Array<LanguageAlternateLink> {
  if (!languageOptions.length) return []

  const segments = path.split('/')
  const firstSegment = segments[1]
  const hasLocaleSegment = !!firstSegment && languageOptions.some(
    option => option.slug === firstSegment || option.locale === firstSegment,
  )
  const barePath = hasLocaleSegment
    ? ['', ...segments.slice(2)].join('/') || '/'
    : path

  return languageOptions.filter(option => option.locale !== DEFAULT_LOCALE).map(option => {
    const region = new Intl.Locale(option.locale).region?.toLowerCase() ?? option.locale.split('-').pop()?.toLowerCase() ?? ''
    const languageSegment = option.slug ?? option.locale
    return {
      hrefLang: option.locale,
      href: `${customerPortalURL}/${region}/${languageSegment}${barePath}`,
    }
  })
}
